import { Button, SimpleGrid } from '@mantine/core';
import { NoteCard } from './Cards/NoteCard';
import { AttachmentCard } from './Cards/AttachmentCard';
import { createFormContext } from '@mantine/form';
import useSWR from 'swr';
import { getFetcher, patchFetcher } from '@/api/fetchers';
import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { isEmptyForm } from '@/utils/functions';
import { type CarePlanDetailed } from '@/schemas/API/GET/client/carePlan/response';
import { type ClientDetailed } from '@/schemas/API/GET/client/response';
import {
  type ClientAttachments,
  type ClientAttachment,
} from '@/schemas/API/GET/client/attachment/response';
import { ServiceContext } from '@/layouts/Person/PersonLayout';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { type Service } from '@/schemas/API/GET/service/response';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { displayErrorOnMantineForm } from '@/utils/error/errorHandler';
import {
  goodNotification,
  okNotification,
} from '@/components/Notification/Notification';
import { PlanNotFound } from '@/pages/Clients/Client/IPP/Cards/PlanNotFound';

export const [CarePlanFormProvider, useCarePlanFormContext, useCarePlanForm] =
  createFormContext<CarePlanDetailed>();

interface IAttachmentForm {
  attachments: ClientAttachment[];
}

export const [
  AttachmentsFormProvider,
  useAttachmentsFormContext,
  useAttachmentsForm,
] = createFormContext<IAttachmentForm>();

export const ClientDetailsSection = (): JSX.Element => {
  const { service } = useContext(ServiceContext);
  const { clientId } = useParams<{ clientId: string }>();

  const { data: client } = useSWR<ClientDetailed>(
    `/clients/${clientId}`,
    getFetcher
  );

  /**
   * Read "Arguments" and "Conditional Data Fetching" pages of useSWR docs before trying to understand the code below.
   *
   * Execute this request <=> client and service are both defined
   * We use array [`/clients/${clientId}/careplans`, service] as the key parameter (means it is refetched every time the service is changed)
   */
  const { data: careplan, mutate } = useSWR<CarePlanDetailed>(
    client && service ? [`/clients/${clientId}/careplans`, service] : null,
    async ([url, service]) => {
      // useSWR is type-unfriendly :(
      // But casting should be safe, as we demand client and service to have value
      const _service = service as Service;
      const _client = client as ClientDetailed;


      const carePlanShort = _client.carePlans.find(
        cp => cp.serviceId === _service.id
      );

      if (!carePlanShort) {
        return undefined;
      }

      return await getFetcher(`${url}/${carePlanShort.carePlanId}`);
    }
  );

  const { data: attachments } = useSWR<ClientAttachments>(
    `/clients/${clientId}/attachments`,
    getFetcher
  );

  const carePlanForm = useCarePlanForm();
  const attachmentForm = useAttachmentsForm();

  useEffect(() => {
    if (!careplan || !service) return;

    carePlanForm.setInitialValues(careplan);
    carePlanForm.setValues(careplan);
  }, [careplan, service]);

  useEffect(() => {
    if (!attachments || !service) return;

    attachmentForm.setFieldValue('attachments', attachments.content);
  }, [attachments, service]);

  const updateDetails = async (): Promise<void> => {
    try {
      await patchFetcher(
        `/clients/${clientId}/careplans/${carePlanForm.values.id}`,
        {
          noteCoord: carePlanForm.values.noteCoord,
          noteCaregiver: carePlanForm.values.noteCaregiver,
        }
      );
      await mutate();
      goodNotification('Změny byly uloženy.');
    } catch (err) {
      displayErrorOnMantineForm(err, carePlanForm.setFieldError);
    }
  };

  function handleCancel(): void {
    carePlanForm.reset();
    okNotification('Storno...', 'Detaily jsou teď v původním stavu!');
  }

  if (!isEmptyForm(carePlanForm) && !isEmptyForm(attachmentForm) && careplan) {
    return (
      <CarePlanFormProvider form={carePlanForm}>
        <SimpleGrid cols={2}>
          <NoteCard noteType="noteCoord" title="Poznámka pro koordinátora" />
          <NoteCard noteType="noteCaregiver" title="Poznámka pro pečovatele" />
        </SimpleGrid>
        <SimpleGrid>
          <AttachmentsFormProvider form={attachmentForm}>
            <AttachmentCard></AttachmentCard>
          </AttachmentsFormProvider>
        </SimpleGrid>
        <ButtonBar>
          <Button
            variant="outline"
            radius="md"
            size="md"
            className="text-lg"
            onClick={handleCancel}
          >
            Storno
          </Button>
          <Button
            radius="md"
            size="md"
            className="text-lg"
            onClick={updateDetails}
          >
            Uložit
          </Button>
        </ButtonBar>
      </CarePlanFormProvider>
    );
  } else if (careplan) return <SectionLoadingOverlay />;

  if (!careplan && clientId) {
    return <PlanNotFound clientId={clientId} />;
  }

  return (
    <div>
      Nenalezen žádný plán pro službu: <b>{service?.name}</b>
    </div>
  );
};
